import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Gift, X, ChevronUp, ChevronDown, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/lib/i18n';
import { useCurrency } from '@/hooks/useCurrency';
import { cn } from '@/lib/utils';
import { useActiveOfferStore } from '@/stores/activeOfferStore';
import { useLocation, useNavigate } from 'react-router-dom';

export function FloatingOfferBanner() {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { formatPrice } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  
  const { activeOffer, clearActiveOffer, isOfferValid, getTimeRemaining } = useActiveOfferStore();
  const [timeLeft, setTimeLeft] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  
  // Don't show on thank-you page or checkout
  const hideOnPages = ['/thank-you', '/checkout'];
  const shouldHide = hideOnPages.some(page => location.pathname.includes(page));
  
  
  useEffect(() => {
    if (!activeOffer) return;
    
    // Check validity based on expiresAt directly
    const now = Date.now();
    if (activeOffer.expiresAt <= now) {
      clearActiveOffer();
      return;
    }
    
    const updateTimer = () => {
      const remaining = Math.max(0, activeOffer.expiresAt - Date.now());
      if (remaining <= 0) {
        clearActiveOffer();
        setTimeLeft(0);
        return;
      }
      setTimeLeft(remaining);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeOffer, clearActiveOffer]);
  
  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const getDiscountText = () => {
    if (!activeOffer) return '';
    switch (activeOffer.discountType) {
      case 'percentage': return `${activeOffer.discountValue}%`;
      case 'fixed': return formatPrice(activeOffer.discountValue);
      case 'free_shipping': return isRTL ? 'شحن مجاني' : 'Free Shipping';
      default: return '';
    }
  };
  
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      clearActiveOffer();
    }, 300);
  };
  
  const handleGoToProducts = () => {
    navigate('/products');
  };
  
  // Check if offer is valid
  const offerIsValid = activeOffer && activeOffer.expiresAt > Date.now();
  
  if (!activeOffer || !offerIsValid || shouldHide || !isVisible) {
    return null;
  }
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-4 inset-x-0 z-50 px-4 pointer-events-none"
      >
        <div className="max-w-md mx-auto pointer-events-auto">
          <motion.div
            layout
            className="rounded-lg shadow-lg overflow-hidden backdrop-blur-md"
            style={{ 
              backgroundColor: activeOffer.backgroundColor || 'hsl(var(--primary))',
              color: activeOffer.textColor || '#ffffff'
            }}
          >
            <div className="flex items-center gap-3 px-3 py-2.5">
              {/* Gift Icon */}
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Gift className="h-4 w-4" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">
                    {getDiscountText()} {isRTL ? 'خصم' : 'OFF'}
                  </span>
                  <span className="text-xs opacity-80 truncate">
                    {isRTL ? 'أضف منتج للطلب السابق' : 'Add to previous order'}
                  </span>
                </div>
              </div>
              
              {/* Timer */}
              <div className="flex items-center gap-1.5 bg-black/20 rounded-full px-2.5 py-1 shrink-0">
                <Clock className="h-3.5 w-3.5" />
                <span className={cn(
                  "font-mono font-bold text-sm",
                  timeLeft < 60000 && "animate-pulse"
                )}>
                  {formatTime(timeLeft)}
                </span>
              </div>
              
              {/* Close */}
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-white/20 transition-colors shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
